import React from 'react'
import {
  Row,
  Col,
  Table,
  Form,
  Button,
  ButtonGroup
} from '@themesberg/react-bootstrap'
import PaginationComponent from '../../components/Pagination'
import {
  faCheckSquare,
  faWindowClose
} from '@fortawesome/pro-solid-svg-icons'

import Trigger from '../../components/OverlayTrigger'
import useCasinoStudiosListing from './hooks/useCasinoStudiosListing'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import ConfirmationModal from '../../components/ConfirmationModal'
import ProviderFilter from '../../components/ProviderFilter'
import AggregatorList from '../../shared/AggregatorList'
import ClientFilter from '../../components/ClientFilter'
import PortalFilter from '../../components/PortalFilter'
import useCheckPermission from '../../utils/checkPermission'

const tableHeaders = [
  {
    label: 'ID',
    value: 'casinoStudioId'
  },
  {
    label: 'Name',
    value: 'name'
  },
  {
    label: 'Provider',
    value: 'casinoProviderId'
  },
  {
    label: 'Status',
    value: 'isActive'
  },
  {
    label: 'Actions',
    value: 'actions'
  }
]

/**
 * Casino Studios component
 * @param {Object} props - Component props
 * @param {boolean} props.isTenant - Whether the current user is a tenant
 * @returns {JSX.Element} - Rendered component
 */
const CasinoStudios = ({ isTenant = true }) => {
  // Use the shared hook with isTenant parameter
  const hookProps = useCasinoStudiosListing(isTenant)
  const { isHidden } = useCheckPermission()

  const {
    limit,
    page,
    loading,
    setLimit,
    setPage,
    totalPages,
    casinoStudios,
    search,
    setSearch,
    statusShow,
    setStatusShow,
    handleStatusShow,
    handleYes,
    status,
    selectedProvider,
    setSelectedProvider,
    name,
    aggregatorsOptions,
    selectedAggregatorId,
    setSelectedAggregatorId
  } = hookProps

  // Get super admin specific props if not tenant
  const {
    selectedClient,
    setSelectedClient,
    selectedPortal,
    setSelectedPortal
  } = !isTenant ? hookProps : {}

  const renderCommonFilters = () => {
    const CommonFilters = (
      <>
        <div className='d-flex align-items-center gap-2'>
          <AggregatorList
            aggregatorOptions={aggregatorsOptions}
            selectedAggregatorId={selectedAggregatorId}
            setSelectedAggregatorId={setSelectedAggregatorId}
            labelClassName='mb-0'
          />
        </div>
        <div className='d-flex align-items-center'>
          <ProviderFilter
            selectedProvider={selectedProvider}
            setSelectedProvider={setSelectedProvider}
            isTenantRoute={isTenant}
            aggregatorId={selectedAggregatorId}
          />
        </div>
        <div className='d-flex align-items-center gap-1'>
          <Form.Label column='sm' style={{ marginBottom: '0' }}>
            Search
          </Form.Label>
          <Form.Control
            name='search'
            type='search'
            placeholder='Search'
            value={search}
            size='sm'
            className='w-90 mx-auto'
            onChange={(e) => {
              setSearch(e.target.value)
            }}
          />
        </div>
      </>
    )

    if (isTenant) {
      return CommonFilters
    }

    if (!isTenant && selectedPortal && selectedClient) {
      return CommonFilters
    }

    return null
  }

  return (
    <>
      <Row>
        <Col xs='12'>
          <h3>Game Studios</h3>
        </Col>

        <Col xs='auto'>
          <div className='d-flex align-items-center flex-wrap gap-3'>
            {/* Render client and portal filters only for super admin */}
            {!isTenant && (
              <>
                <div className='d-flex align-items-center gap-2'>
                  <ClientFilter
                    selectedClient={selectedClient}
                    setSelectedClient={setSelectedClient}
                    setSelectedPortal={setSelectedPortal}
                    hasAllOptions={false}
                  />
                </div>
                <div className='d-flex align-items-center gap-2'>
                  <PortalFilter
                    selectedClient={selectedClient}
                    selectedPortal={selectedPortal}
                    setSelectedPortal={setSelectedPortal}
                    hasAllOptions={false}
                  />
                </div>
              </>
            )}
            {renderCommonFilters()}
          </div>
        </Col>
      </Row>

      <Table bordered striped responsive hover size='sm' className='text-center mt-4'>
        <thead className='thead-dark'>
          <tr>
            {tableHeaders.map((h, idx) => (
              <th key={idx}>
                {h.label}
              </th>
            ))}
          </tr>
        </thead>

        <tbody id={loading ? 'cover-spin' : ''}>
          {/* Conditional rendering based on user type and data availability */}
          {!loading &&
          // For super admin, only show data if portal is selected
          (isTenant || (!isTenant && selectedPortal)) &&
          casinoStudios?.count > 0 &&
          (Array.isArray(casinoStudios.rows)
            ? casinoStudios.rows
            : casinoStudios).map(
            (studio) => {
              const {
                casinoStudioId,
                name,
                isActive,
                providerName
              } = studio

              return (
                <tr key={casinoStudioId}>
                  <td>{casinoStudioId}</td>
                  <td>{name}</td>

                  <td className='text-capitalize'>
                    {providerName}
                  </td>

                  <td>
                    {isActive
                      ? <span className='text-success'>Active</span>
                      : <span className='text-danger'>In Active</span>}
                  </td>

                  <td>
                    <ButtonGroup>
                      {!isActive
                        ? (
                          <Trigger message='Set Studio Status Active'>
                            <Button
                              className='m-1'
                              size='sm'
                              variant='success'
                              hidden={isHidden({ module: { key: 'CasinoStudio', value: 'T' } })}
                              onClick={() =>
                                handleStatusShow(casinoStudioId, isActive, name)}
                            >
                              <FontAwesomeIcon icon={faCheckSquare} />
                            </Button>
                          </Trigger>
                          )
                        : (
                          <Trigger message='Set Studio Status In-Active'>
                            <Button
                              className='m-1'
                              size='sm'
                              variant='danger'
                              hidden={isHidden({ module: { key: 'CasinoStudio', value: 'T' } })}
                              onClick={() =>
                                handleStatusShow(casinoStudioId, isActive, name)}
                            >
                              <FontAwesomeIcon icon={faWindowClose} />
                            </Button>
                          </Trigger>
                          )}
                    </ButtonGroup>
                  </td>
                </tr>
              )
            }
          )}

          {/* Show appropriate message when no data is available */}
          {(
          // For tenant: show message when no data or loading is complete
            (isTenant && (!casinoStudios || casinoStudios?.count === 0) && !loading) ||
          // For super admin: show message when no portal selected or no data and loading is complete
          (!isTenant && (!selectedPortal || !casinoStudios || casinoStudios?.count === 0) && !loading)
          ) && (
            <tr>
              <td
                colSpan={5}
                className='text-danger text-center'
              >
                {!isTenant && !selectedPortal
                  ? 'Please select a portal and tenant to view data'
                  : 'No data found'}
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {/* Pagination component - only show when data is available */}
      {!loading &&
        (isTenant || (!isTenant && selectedPortal)) &&
        casinoStudios?.count > 0 && (
          <PaginationComponent
            page={casinoStudios?.count < page ? setPage(1) : page}
            totalPages={totalPages}
            setPage={setPage}
            limit={limit}
            setLimit={setLimit}
          />
      )}

      {/* Confirmation modal */}
      {statusShow && (
        <ConfirmationModal
          setShow={setStatusShow}
          show={statusShow}
          handleYes={handleYes}
          active={status}
          name={name}
        />
      )}
    </>
  )
}

export default CasinoStudios
